import { GenericLazyString } from '@eventbrite/i18n';
import { useMemo } from 'react';
import { TagAggApiResponse } from '../../../../api/searchOrganicEvents';
import { useLocationAggregations } from './aggregations';
import { useBaseEventsQuery } from './events';

type PopularTags = string[];

const sortByRanking = (
    allTagsOfType: Record<string, GenericLazyString | string>,
    tagsByLocation: { buckets: TagAggApiResponse[] },
    type: 'Category' | 'Format' | 'SubCategory',
): PopularTags => {
    const tags = tagsByLocation.buckets;

    const setOfSortedTags = new Set();
    const tagsOfType = tags
        .filter(
            (tag) =>
                tag.key.includes(`Eventbrite${type}`) &&
                !tag.display_name.includes('Other'),
        )
        .map((tag) => {
            setOfSortedTags.add(tag.key);
            return tag.key;
        });

    const missingTags = Object.keys(allTagsOfType).reduce((memo, key) => {
        if (
            !setOfSortedTags.has(key) &&
            !allTagsOfType[key].toString().includes('Other')
        ) {
            return [...memo, key];
        }

        return memo;
    }, [] as PopularTags);

    return [...tagsOfType, ...missingTags];
};

const useMemoizedTags = (
    allTags: Record<string, GenericLazyString>,
    type: 'Category' | 'Format' | 'SubCategory',
    data?: { buckets: TagAggApiResponse[] },
) => {
    const popularTags = useMemo(() => {
        return Object.keys(allTags)
            .filter(
                (key) =>
                    key !== 'EventbriteCategory/199' &&
                    !allTags[key].toString().includes('Other'),
            )
            .map((key) => key);
    }, [allTags]);

    const sortedPopularTags = useMemo(() => {
        if (data) {
            return sortByRanking(allTags, data, type);
        }

        return [];
    }, [data, allTags, type]);

    return sortedPopularTags.length ? sortedPopularTags : popularTags;
};

/**
 * This hook will return a default list of tags. However, once data is present will
 * return the provided type sorted by the events associated to the current filters.
 *
 * ie: Music events in San Francisco: It will return a sorted list of tags representing
 * the most common events.
 *
 * @param allTags A dictionary that represents a full set of tags representative
 * of the current type. The key should be the ID of format `Eventbrite{Type}/{id}`
 * and the value should equal a display string.
 * @param type The type of tag to filter the results for.
 * @returns string[] of tag keys
 */
export function useAllTagsSortedByActiveCategory(
    allTags: Record<string, GenericLazyString>,
    type: 'Category' | 'Format' | 'SubCategory',
) {
    const baseEventsQuery = useBaseEventsQuery(
        (data) => data.events.aggs,
        {},
        { optOutDedupeEvents: true },
    );
    const results = useMemoizedTags(allTags, type, baseEventsQuery.data?.tags);

    return results;
}

/**
 * This hook will initially return a default list of tags.
 *
 * However, once data is present will return tags of type
 * sorted by number events associated to the current location.
 *
 * ie: Events in San Francisco: It will return a sorted list of tags representing
 * the most common events.
 *
 * @param allTags A dictionary that represents a full set of tags representative
 * of the current type. The key should be the ID of format `Eventbrite{Type}/{id}`
 * and the value should equal a display string.
 * @param type The type of tag to filter the results for.
 * @returns string[] of tag keys
 */
export function useAllTagsSortedByLocation(
    allTags: Record<string, GenericLazyString>,
    type: 'Category' | 'Format' | 'SubCategory',
): PopularTags {
    const { data } = useLocationAggregations();
    const results = useMemoizedTags(allTags, type, data?.tags);

    return results;
}
